import React, { useState, useEffect } from 'react';
import { Percent, ChevronDown, ChevronUp, Filter, Info } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getAllDataPaginated } from '../../lib/supabaseUtils';
import { useAuth } from '../../hooks/useAuth';
import { useCollection } from '../../hooks/useCollection';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { Loader2 } from 'lucide-react';
import { PackProbabilityStats, PokemonCard, UserCard, RarityStats, ProbabilityFilterOptions } from '../../types/pokemon';
import { useTradeSettings } from '../../hooks/useTradeSettings';
import { useI18n } from '../../contexts/I18nContext';
import { useNavigate } from 'react-router-dom';
import { cardsCache, generateCardsCacheKey } from '../../lib/cacheUtils';

interface GlobalPackProbabilitiesProps {
  excludeSets?: string[];
  filterOptions?: ProbabilityFilterOptions;
  onFilterChange?: (options: ProbabilityFilterOptions) => void;
}

interface SetInfo {
  id: string;
  display_name: string;
  display_name_en?: string;
  img: string | null;
  name?: string;
}

interface PackWithSet extends PackProbabilityStats {
  setName: string;
  setId: string;
  setImg: string | null;
  pack: string;
  packImage: string | null;
}

// Nouvelles probabilités par position selon spec_stat.md
const POSITION_PROBABILITIES_WITH_SHINY: Record<string, Record<string, number>> = {
  // Positions 1-3 (premières cartes)
  'first_three': {
    '♢': 1.0,    // 100%
    '♢♢': 0.0,   // 0%
    '♢♢♢': 0.0,  // 0%
    '♢♢♢♢': 0.0, // 0%
    '☆': 0.0,    // 0%
    '☆☆': 0.0,   // 0%
    '☆☆☆': 0.0,  // 0%
    '✧': 0.0,    // 0%
    '✧✧': 0.0,   // 0%
    '♛': 0.0,    // 0%
  },
  // Position 4
  'fourth': {
    '♢': 0.0,      // 0%
    '♢♢': 0.89,    // 89%
    '♢♢♢': 0.05,   // 5%
    '♢♢♢♢': 0.0167, // 1.67%
    '☆': 0.0257,   // 2.57%
    '☆☆': 0.005,   // 0.5%
    '☆☆☆': 0.0022, // 0.22%
    '✧': 0.00714,  // 0.714%
    '✧✧': 0.00333, // 0.333%
    '♛': 0.0004,   // 0.04%
  },
  // Position 5
  'fifth': {
    '♢': 0.0,      // 0%
    '♢♢': 0.56,    // 56%
    '♢♢♢': 0.19810,// 19.810%
    '♢♢♢♢': 0.0664,// 6.66%
    '☆': 0.1029,   // 10.29%
    '☆☆': 0.02,    // 2%
    '☆☆☆': 0.0089, // 0.89%
    '✧': 0.02857,  // 2.857%
    '✧✧': 0.01333, // 1.333%
    '♛': 0.0016,   // 0.16%
  }
};

// Anciennes probabilités pour les packs sans shiny
const POSITION_PROBABILITIES_NO_SHINY: Record<string, Record<string, number>> = {
  // Positions 1-3 (premières cartes)
  'first_three': {
    '♢': 1.0,    // 100%
    '♢♢': 0.0,   // 0%
    '♢♢♢': 0.0,  // 0%
    '♢♢♢♢': 0.0, // 0%
    '☆': 0.0,    // 0%
    '☆☆': 0.0,   // 0%
    '☆☆☆': 0.0,  // 0%
    '♛': 0.0,    // 0%
  },
  // Position 4
  'fourth': {
    '♢': 0.0,      // 0%
    '♢♢': 0.9,     // 90%
    '♢♢♢': 0.05,   // 5%
    '♢♢♢♢': 0.0167, // 1.67%
    '☆': 0.0257,   // 2.57%
    '☆☆': 0.005,   // 0.5%
    '☆☆☆': 0.0022, // 0.22%
    '♛': 0.0004,   // 0.04%
  },
  // Position 5
  'fifth': {
    '♢': 0.0,      // 0%
    '♢♢': 0.6,     // 60%
    '♢♢♢': 0.2,    // 20%
    '♢♢♢♢': 0.0666, // 6.66%
    '☆': 0.1029,   // 10.29%
    '☆☆': 0.02,    // 2%
    '☆☆☆': 0.0089, // 0.89%
    '♛': 0.0016,   // 0.16%
  }
};

// Formater un pourcentage avec 2 décimales
const formatPercent = (value: number) => {
  // S'assurer que la valeur est entre 0 et 1
  const clampedValue = Math.max(0, Math.min(1, value));
  
  // Convertir en pourcentage avec 2 décimales
  const percentage = (clampedValue * 100).toFixed(2);
  
  // Supprimer les zéros inutiles à la fin (ex: 25.00% -> 25%)
  const formattedPercentage = parseFloat(percentage).toString();
  
  return formattedPercentage + '%';
};

// Fonction utilitaire pour normaliser les symboles d'étoiles
const normalizeRaritySymbol = (rarity: string): string => {
  // Convertir les symboles ★ en ☆ pour la correspondance avec POSITION_PROBABILITIES
  if (rarity === '★') return '☆';
  if (rarity === '★★') return '☆☆';
  if (rarity === '★★★') return '☆☆☆';
  
  // Convertir les symboles ☆ en ★ si nécessaire (selon le format utilisé)
  // if (rarity === '☆') return '★';
  // if (rarity === '☆☆') return '★★';
  // if (rarity === '☆☆☆') return '★★★';
  
  return rarity;
};

// Remplacer la constante POSITION_PROBABILITIES par une fonction qui retourne les bonnes probabilités
const getPositionProbabilities = (cards: PokemonCard[]): Record<string, Record<string, number>> => {
  // Vérifier si le pack contient des cartes shiny
  const hasShinyCards = cards.some(card => card.rarity === '✧' || card.rarity === '✧✧');
  return hasShinyCards ? POSITION_PROBABILITIES_WITH_SHINY : POSITION_PROBABILITIES_NO_SHINY;
};

export function GlobalPackProbabilities({ excludeSets = ['promo-a'], filterOptions, onFilterChange }: GlobalPackProbabilitiesProps) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sets, setSets] = useState<SetInfo[]>([]);
  const [allCards, setAllCards] = useState<Record<string, PokemonCard[]>>({});
  const [globalPackStats, setGlobalPackStats] = useState<PackWithSet[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const { collection } = useCollection();
  const { user } = useAuth();
  const { data: tradeSettings } = useTradeSettings();
  
  // Options de filtrage avancé
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Options pour afficher/masquer les informations sur les probabilités
  const [showProbabilityInfo, setShowProbabilityInfo] = useState(false);

  // Ajouter juste après les autres états (vers la ligne 110)
  const [refreshing, setRefreshing] = useState(false);

  // Ajouter cette fonction dans le composant
  const refreshData = async () => {
    // Éviter les rafraîchissements multiples
    if (refreshing || loading) return;
    
    setRefreshing(true);
    
    try {
      // Vider le cache des cartes et des packs
      cardsCache.delete(generateCardsCacheKey({ action: 'getAllCards' }));
      cardsCache.delete(generateCardsCacheKey({ action: 'getAllPacks' }));
      
      // Recharger la page
      window.location.reload();
    } finally {
      setRefreshing(false);
    }
  };

  // Charger tous les sets
  useEffect(() => {
    const fetchSets = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('sets')
          .select('id, name, display_name, display_name_en, img')
          .order('id');
        
        if (error) {
          throw error;
        }
        
        if (!data || data.length === 0) {
          setLoading(false);
          return;
        }
        
        // Filtrer les sets exclus
        const filteredSets = data.filter(set => !excludeSets.includes(set.id));
        
        setSets(filteredSets);
        
        // Créer un mapping entre les noms des sets et leurs IDs
        const setNameToIdMap: Record<string, string> = {};
        
        // Ajouter d'abord les mappings directs ID -> ID
        filteredSets.forEach(set => {
          setNameToIdMap[set.id] = set.id;
        });
        
        // Ajouter ensuite les mappings name -> ID si disponibles
        filteredSets.forEach(set => {
          if (set.name) {
            setNameToIdMap[set.name] = set.id;
            // Ajouter également des versions normalisées (sans tirets, en minuscules)
            setNameToIdMap[set.name.toLowerCase().replace(/-/g, '')] = set.id;
          }
          if (set.display_name) {
            // Utiliser aussi display_name comme clé possible
            setNameToIdMap[set.display_name.toLowerCase().replace(/ /g, '-')] = set.id;
          }
        });
        
        // Initialiser l'objet allCards avec des tableaux vides pour chaque set
        const cardsObj: Record<string, PokemonCard[]> = {};
        filteredSets.forEach(set => {
          cardsObj[set.id] = [];
        });
        
        // Vérifier d'abord si nous avons les cartes dans le cache
        const cacheKey = generateCardsCacheKey({ action: 'getAllCards' });
        const cachedCards = cardsCache.get(cacheKey);
        
        if (cachedCards) {
          console.log('Utilisation des cartes mises en cache');
          // Répartir les cartes du cache par set
          cachedCards.forEach(card => {
            if (card.set && !excludeSets.includes(card.set) && cardsObj[card.set]) {
              cardsObj[card.set].push(card);
            } else if (card.set && setNameToIdMap[card.set] && !excludeSets.includes(setNameToIdMap[card.set])) {
              const setId = setNameToIdMap[card.set];
              cardsObj[setId].push(card);
            }
          });
          
          setAllCards(cardsObj);
          setLoading(false);
          return;
        }
        
        // Si nous n'avons pas les cartes dans le cache, les récupérer depuis Supabase
        try {
          console.log('Récupération des cartes depuis Supabase avec pagination');
          
          const allCardsData = await getAllDataPaginated<PokemonCard>('cards');
          
          // Stocker toutes les cartes dans le cache pour une utilisation future
          if (allCardsData) {
            cardsCache.set(cacheKey, allCardsData);
            
            // Répartir les cartes par set en utilisant le mapping
            allCardsData.forEach(card => {
              // Vérifier si la carte appartient à un set exclu
              let isExcludedSet = false;
              
              // Vérifier directement si le set est exclu
              if (card.set && excludeSets.includes(card.set)) {
                isExcludedSet = true;
              } 
              // Vérifier via le mapping si le set correspond à un set exclu
              else if (card.set) {
                // Normaliser le nom du set pour la comparaison
                const normalizedSet = card.set.toLowerCase().replace(/-/g, '');
                
                // Vérifier si ce set normalisé correspond à un set exclu
                for (const excludedSet of excludeSets) {
                  const normalizedExcludedSet = excludedSet.toLowerCase().replace(/-/g, '');
                  if (normalizedSet === normalizedExcludedSet) {
                    isExcludedSet = true;
                    break;
                  }
                }
              }
              
              // Si la carte n'appartient pas à un set exclu, l'ajouter à cardsObj
              if (!isExcludedSet) {
                // Pour chaque carte, vérifier si son set correspond à un ID de set
                let found = false;
                
                // Vérifier d'abord si le set de la carte correspond directement à un ID de set
                if (card.set && cardsObj[card.set]) {
                  cardsObj[card.set].push(card);
                  found = true;
                } 
                // Vérifier si le set de la carte correspond à une entrée dans notre mapping
                else if (card.set && setNameToIdMap[card.set]) {
                  const setId = setNameToIdMap[card.set];
                  cardsObj[setId].push(card);
                  found = true;
                }
                // Vérifier avec une version normalisée du set
                else if (card.set) {
                  const normalizedSet = card.set.toLowerCase().replace(/-/g, '');
                  if (setNameToIdMap[normalizedSet]) {
                    const setId = setNameToIdMap[normalizedSet];
                    cardsObj[setId].push(card);
                    found = true;
                  } else {
                    // Parcourir tous les sets pour voir si le nom du set correspond
                    for (const set of filteredSets) {
                      // Comparer le set de la carte avec le nom du set (en minuscules et sans accents)
                      if (set.name && set.name.toLowerCase().replace(/-/g, '') === normalizedSet) {
                        cardsObj[set.id].push(card);
                        found = true;
                        break;
                      }
                    }
                  }
                }
              }
            });
          }
        } catch (error) {
          // Erreur silencieuse
          console.debug('Erreur lors du chargement des cartes:', error);
        }
        
        setAllCards(cardsObj);
      } catch (error) {
        // Erreur silencieuse
        console.debug('Erreur lors du chargement des sets:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSets();
  }, [user, excludeSets]);

  // Calculer les probabilités pour tous les packs de tous les sets
  useEffect(() => {
    const calculateAllProbabilities = async () => {
      if (!user || Object.keys(allCards).length === 0) {
        return;
      }
      
      try {
        const allPackStats: PackWithSet[] = [];
        
        // Récupérer tous les packs en une seule requête avec cache
        const cacheKey = generateCardsCacheKey({ action: 'getAllPacks' });
        const cachedPacks = cardsCache.get(cacheKey);
        
        let allPacksData;
        
        if (cachedPacks) {
          console.log('Utilisation des packs mis en cache');
          allPacksData = cachedPacks;
        } else {
          console.log('Récupération des packs depuis Supabase');
          const { data, error } = await supabase
            .from('packs')
            .select('*');
          
          if (error) {
            throw error;
          }
          
          // Mettre en cache les packs pour une utilisation future
          if (data) {
            cardsCache.set(cacheKey, data);
            allPacksData = data;
          }
        }
        
        if (!allPacksData) {
          return;
        }
        
        // Créer un mapping entre les noms des sets et leurs IDs
        const setNameToIdMap: Record<string, string> = {};
        
        // Ajouter d'abord les mappings directs ID -> ID
        sets.forEach((set: SetInfo) => {
          setNameToIdMap[set.id] = set.id;
        });
        
        // Ajouter ensuite les mappings name -> ID si disponibles
        sets.forEach((set: SetInfo) => {
          if (set.name) {
            setNameToIdMap[set.name] = set.id;
            // Ajouter également des versions normalisées (sans tirets, en minuscules)
            setNameToIdMap[set.name.toLowerCase().replace(/-/g, '')] = set.id;
          }
          if (set.display_name) {
            // Utiliser aussi display_name comme clé possible
            setNameToIdMap[set.display_name.toLowerCase().replace(/ /g, '-')] = set.id;
          }
        });
        
        // Organiser les packs par set
        const packsBySet: Record<string, PackProbabilityStats[]> = {};
        // let packsAssigned = 0;
        // let packsNotFound = 0;
        // let packsExcluded = 0;
        
        if (allPacksData) {
          allPacksData.forEach(pack => {
            // Vérifier si le pack appartient à un set exclu
            let isExcludedSet = false;
            
            // Vérifier directement si le set est exclu
            if (pack.set && excludeSets.includes(pack.set)) {
              // packsExcluded++;
              isExcludedSet = true;
            } 
            // Vérifier via le mapping si le set correspond à un set exclu
            else if (pack.set) {
              // Normaliser le nom du set pour la comparaison
              const normalizedSet = pack.set.toLowerCase().replace(/-/g, '');
              
              // Vérifier si ce set normalisé correspond à un set exclu
              for (const excludedSet of excludeSets) {
                const normalizedExcludedSet = excludedSet.toLowerCase().replace(/-/g, '');
                if (normalizedSet === normalizedExcludedSet) {
                  // packsExcluded++;
                  isExcludedSet = true;
                  break;
                }
              }
            }
            
            // Si le pack n'appartient pas à un set exclu, l'ajouter à packsBySet
            if (!isExcludedSet && pack.set) {
              let found = false;
              
              // Vérifier d'abord si le set du pack correspond directement à un ID de set
              if (packsBySet[pack.set]) {
                packsBySet[pack.set].push(pack);
                // packsAssigned++;
                found = true;
              }
              // Vérifier si le set du pack correspond à une entrée dans notre mapping
              else if (setNameToIdMap[pack.set]) {
                const setId = setNameToIdMap[pack.set];
                if (!packsBySet[setId]) {
                  packsBySet[setId] = [];
                }
                packsBySet[setId].push(pack);
                // packsAssigned++;
                found = true;
              }
              // Vérifier avec une version normalisée du set
              else {
                const normalizedSet = pack.set.toLowerCase().replace(/-/g, '');
                if (setNameToIdMap[normalizedSet]) {
                  const setId = setNameToIdMap[normalizedSet];
                  if (!packsBySet[setId]) {
                    packsBySet[setId] = [];
                  }
                  packsBySet[setId].push(pack);
                  // packsAssigned++;
                } else {
                  if (!packsBySet[pack.set]) {
                    packsBySet[pack.set] = [];
                  }
                  packsBySet[pack.set].push(pack);
                  // packsNotFound++;
                }
              }
            }
          });
        }
        
        // Pour chaque set
        for (const set of sets) {
          // Vérifier si ce set est exclu
          if (excludeSets.includes(set.id)) {
            continue;
          }
          
          // Vérifier si nous avons des cartes pour ce set
          if (!allCards[set.id] || allCards[set.id].length === 0) {
            continue;
          }
          
          // Récupérer les packs pour ce set
          const setPacks = packsBySet[set.id] || [];
          
          if (setPacks.length === 0) {
            continue;
          }
          
          // Créer une fonction de calcul de probabilités pour ce set
          const calculateProbabilitiesForSet = (cards: PokemonCard[], userCollection: Record<number, UserCard>) => {
            if (!cards || cards.length === 0) {
              return [];
            }
            
            try {
              // Trouver les cartes "All"
              const allCards = cards.filter(card => 
                card.pack?.toLowerCase() === 'all'
              );
              
              // Récupérer les noms des packs pour ce set
              const packNames = setPacks.map(pack => pack.name);
              
              if (packNames.length === 0) {
                return [];
              }
              
              // Calculer les statistiques pour chaque pack du set
              return packNames.map(packName => {
                // Filtrer les cartes spécifiques à ce pack + les cartes "All"
                const packCards = [
                  ...cards.filter(card => card.pack === packName),
                  ...allCards
                ];
                
                // Obtenir les probabilités appropriées pour ce pack
                const positionProbabilities = getPositionProbabilities(packCards);
                
                // Si aucune carte n'est disponible pour ce pack, retourner des probabilités nulles
                if (packCards.length === 0) {
                  return {
                    pack: packName,
                    packImage: null,
                    firstThreeCards: 0,
                    fourthCard: 0,
                    fifthCard: 0,
                    global: 0,
                    rarityStats: {}
                  };
                }
                
                // Vérifier si une carte est éligible à l'échange
                const isCardTradable = (card: PokemonCard) => {
                  if (!tradeSettings) return false;
                  
                  return tradeSettings.some(rule => 
                    rule.eligible_sets.includes(card.set || '') && 
                    rule.eligible_rarities.includes(card.rarity)
                  );
                };
                
                // Initialiser les statistiques par rareté
                const rarityStats: Record<string, RarityStats> = {};
                
                // Calculer les statistiques pour chaque rareté
                packCards.forEach(card => {
                  // Normaliser la rareté de la carte
                  const normalizedRarity = normalizeRaritySymbol(card.rarity);
                  
                  if (!rarityStats[normalizedRarity]) {
                    rarityStats[normalizedRarity] = {
                      total: 0,
                      owned: 0,
                      missing: 0,
                      lessThanTwo: 0,
                      tradable: 0
                    };
                  }
                  
                  rarityStats[normalizedRarity].total++;
                  
                  const userCard = userCollection[card.id];
                  const cardTradable = isCardTradable(card);
                  
                  if (userCard && userCard.quantity > 0) {
                    rarityStats[normalizedRarity].owned++;
                    
                    // Compter les cartes possédées en moins de 2 exemplaires
                    if (userCard.quantity < 2) {
                      rarityStats[normalizedRarity].lessThanTwo++;
                    }
                    
                    // Compter les cartes disponibles à l'échange
                    if (cardTradable) {
                      rarityStats[normalizedRarity].tradable++;
                    }
                  } else {
                    rarityStats[normalizedRarity].missing++;
                  }
                });
                
                // Si la collection est vide, toutes les cartes sont manquantes
                const collectionIsEmpty = Object.keys(userCollection).length === 0;
                
                // Cas particulier : si toutes les cartes sont manquantes ou si la collection est vide
                const allCardsMissing = collectionIsEmpty || Object.values(rarityStats).every(stats => stats.owned === 0);
                
                if (allCardsMissing) {
                  // Si la collection est vide, définir toutes les cartes comme manquantes
                  if (collectionIsEmpty) {
                    Object.keys(rarityStats).forEach(rarity => {
                      rarityStats[rarity].missing = rarityStats[rarity].total;
                      rarityStats[rarity].owned = 0;
                    });
                  }
                  
                  return {
                    pack: packName,
                    packImage: null, // Nous mettrons à jour cette valeur plus tard
                    firstThreeCards: 1,
                    fourthCard: 1,
                    fifthCard: 1,
                    global: 1,
                    rarityStats
                  };
                }
                
                // Calculer les probabilités par position en fonction des options de filtrage
                let firstThreeCardsProbability = 0;
                let fourthCardProbability = 0;
                let fifthCardProbability = 0;
                
                // Calculer la probabilité pour les 3 premières cartes (toujours ♢)
                const firstThreeRarity = '♢';
                if (rarityStats[firstThreeRarity] && rarityStats[firstThreeRarity].total > 0) {
                  // Calculer le nombre de cartes intéressantes selon les critères de filtrage
                  let interestingCards = 0;
                  
                  if (filterOptions?.showMissing) {
                    interestingCards += rarityStats[firstThreeRarity].missing;
                  }
                  
                  if (filterOptions?.showLessThanTwo) {
                    interestingCards += rarityStats[firstThreeRarity].lessThanTwo;
                  }
                  
                  // Exclure les cartes tradables si demandé
                  if (filterOptions?.excludeTradable) {
                    interestingCards -= rarityStats[firstThreeRarity].tradable;
                  }
                  
                  // S'assurer que le nombre n'est pas négatif
                  interestingCards = Math.max(0, interestingCards);
                  
                  const interestingRatio = interestingCards / rarityStats[firstThreeRarity].total;
                  // Probabilité d'obtenir au moins une carte intéressante dans les 3 premières positions
                  firstThreeCardsProbability = 1 - Math.pow(1 - interestingRatio, 3);
                }
                
                // Calculer la probabilité pour la 4ème carte
                Object.entries(positionProbabilities.fourth).forEach(([rarity, probability]) => {
                  // Normaliser la rareté pour la comparaison avec les cartes
                  const normalizedRarity = normalizeRaritySymbol(rarity);
                  
                  // Vérifier si nous avons des cartes de cette rareté (normalisée ou non)
                  const rarityToCheck = rarityStats[normalizedRarity] ? normalizedRarity : rarity;
                  
                  if (rarityStats[rarityToCheck] && rarityStats[rarityToCheck].total > 0 && probability > 0) {
                    // Calculer le nombre de cartes intéressantes selon les critères de filtrage
                    let interestingCards = 0;
                    
                    if (filterOptions?.showMissing) {
                      interestingCards += rarityStats[rarityToCheck].missing;
                    }
                    
                    if (filterOptions?.showLessThanTwo) {
                      interestingCards += rarityStats[rarityToCheck].lessThanTwo;
                    }
                    
                    // Exclure les cartes tradables si demandé
                    if (filterOptions?.excludeTradable) {
                      interestingCards -= rarityStats[rarityToCheck].tradable;
                    }
                    
                    // S'assurer que le nombre n'est pas négatif
                    interestingCards = Math.max(0, interestingCards);
                    
                    const interestingRatio = interestingCards / rarityStats[rarityToCheck].total;
                    fourthCardProbability += probability * interestingRatio;
                  }
                });
                
                // Calculer la probabilité pour la 5ème carte
                Object.entries(positionProbabilities.fifth).forEach(([rarity, probability]) => {
                  // Normaliser la rareté pour la comparaison avec les cartes
                  const normalizedRarity = normalizeRaritySymbol(rarity);
                  
                  // Vérifier si nous avons des cartes de cette rareté (normalisée ou non)
                  const rarityToCheck = rarityStats[normalizedRarity] ? normalizedRarity : rarity;
                  
                  if (rarityStats[rarityToCheck] && rarityStats[rarityToCheck].total > 0 && probability > 0) {
                    // Calculer le nombre de cartes intéressantes selon les critères de filtrage
                    let interestingCards = 0;
                    
                    if (filterOptions?.showMissing) {
                      interestingCards += rarityStats[rarityToCheck].missing;
                    }
                    
                    if (filterOptions?.showLessThanTwo) {
                      interestingCards += rarityStats[rarityToCheck].lessThanTwo;
                    }
                    
                    // Exclure les cartes tradables si demandé
                    if (filterOptions?.excludeTradable) {
                      interestingCards -= rarityStats[rarityToCheck].tradable;
                    }
                    
                    // S'assurer que le nombre n'est pas négatif
                    interestingCards = Math.max(0, interestingCards);
                    
                    const interestingRatio = interestingCards / rarityStats[rarityToCheck].total;
                    fifthCardProbability += probability * interestingRatio;
                  }
                });
                
                // Probabilité globale d'obtenir au moins une nouvelle carte dans un pack
                const globalProbability = 1 - (1 - firstThreeCardsProbability) * (1 - fourthCardProbability) * (1 - fifthCardProbability);
                
                return {
                  pack: packName,
                  packImage: null, // Nous mettrons à jour cette valeur plus tard
                  firstThreeCards: firstThreeCardsProbability,
                  fourthCard: fourthCardProbability,
                  fifthCard: fifthCardProbability,
                  global: globalProbability,
                  rarityStats
                };
              });
            } catch (error) {
              return [];
            }
          };
          
          // Calculer les probabilités pour ce set
          const packStats = calculateProbabilitiesForSet(allCards[set.id], collection);
          
          // Ajouter les informations du set et des packs à chaque pack
          const packsWithSet = packStats.map(pack => {
            // Trouver l'image du pack
            const packInfo = setPacks.find(p => p.name === pack.pack);
            
            return {
              ...pack,
              packImage: packInfo?.img || null,
              setName: lang === 'en' ? (set.display_name_en || set.display_name) : set.display_name,
              setId: set.id,
              setImg: set.img
            } as PackWithSet;
          });
          
          allPackStats.push(...packsWithSet);
        }
        
        // Trier par probabilité globale (décroissant)
        allPackStats.sort((a, b) => b.global - a.global);
        
        setGlobalPackStats(allPackStats);
      } catch (error) {
        // Erreur silencieuse
      }
    };
    
    calculateAllProbabilities();
  }, [sets, allCards, collection, user, filterOptions]);

  // Ajouter un effet pour vérifier si les données sont chargées
  useEffect(() => {
    // Effet silencieux pour vérifier l'état
  }, [loading, sets, allCards, globalPackStats]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-indigo-600">{t('sets.boosters.loading')}</span>
      </div>
    );
  }

  // Si nous n'avons pas de sets, afficher un message approprié
  if (sets.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <Percent className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('sets.boosters.no_sets')}</h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('sets.boosters.no_sets_description')}
        </p>
      </div>
    );
  }

  // Si nous avons des sets mais pas de statistiques
  if (globalPackStats.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <Percent className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('sets.boosters.no_data.title')}</h3>
        <p className="mt-1 text-sm text-gray-500">
          {Object.keys(collection).length === 0 
            ? t('sets.boosters.no_data.collection_empty')
            : t('sets.boosters.no_data.unable_to_calculate')}
        </p>
        <div className="mt-4">
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            {t('sets.boosters.refresh')}
          </button>
        </div>
      </div>
    );
  }

  const bestPack = globalPackStats[0];
  const otherPacks = globalPackStats.slice(1);

  return (
    <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Percent className="w-5 h-5 text-indigo-600" />
          {t('sets.boosters.recommendation_title')}
        </h2>
        <button
          onClick={refreshData}
          disabled={refreshing || loading}
          className={`flex items-center gap-1 px-3 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-md text-sm transition-colors ${
            refreshing || loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <svg className={`w-4 h-4 ${refreshing || loading ? 'animate-spin' : ''}`} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4V9H4.58152M19.9381 11C19.446 7.05369 16.0796 4 12 4C8.64262 4 5.76829 6.06817 4.58152 9M4.58152 9H9M20 20V15H19.4185M19.4185 15C18.2317 17.9318 15.3574 20 12 20C7.92038 20 4.55399 16.9463 4.06189 13M19.4185 15H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {refreshing || loading 
            ? t('sets.boosters.refreshing') 
            : t('sets.boosters.refresh_data')}
        </button>
      </div>
      
      <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-100 text-xs sm:text-sm text-blue-800">
        <p className="font-medium text-blue-900">{t('sets.boosters.recommendation_description')}</p>
        
        {/* Bouton pour afficher/masquer les informations sur les probabilités */}
        <div className="mt-3">
          <div 
            className="bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-3 rounded-lg shadow-sm cursor-pointer"
            onClick={() => setShowProbabilityInfo(!showProbabilityInfo)}
          >
            <h3 className="text-sm font-semibold text-white flex items-center justify-between">
              <div className="flex items-center">
                <Info className="w-4 h-4 mr-2" />
                {t('sets.boosters.probabilities_title')}
              </div>
              {showProbabilityInfo ? (
                <ChevronUp className="w-4 h-4 text-white" />
              ) : (
                <ChevronDown className="w-4 h-4 text-white" />
              )}
            </h3>
          </div>
          
          {showProbabilityInfo && (
            <div className="mt-3 p-4 sm:p-5 bg-white rounded-lg border border-indigo-100 shadow-sm">
              <h4 className="text-sm sm:text-base font-semibold text-indigo-800 mb-3">{t('sets.boosters.position_rarity')}</h4>
              <p className="text-xs sm:text-sm text-gray-600 mb-4">{t('sets.boosters.booster_content')}</p>
              
              <div className="space-y-5">
                {/* Positions 1-3 */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-100">
                  <h5 className="text-xs sm:text-sm font-semibold text-indigo-700 mb-2 flex items-center">
                    <span className="inline-flex items-center justify-center bg-indigo-100 text-indigo-800 w-5 h-5 sm:w-6 sm:h-6 rounded-full mr-2 font-bold text-xs sm:text-sm">1-3</span>
                    {t('sets.boosters.positions_1_3')}
                  </h5>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white p-2 rounded-md shadow-sm flex items-center justify-between">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">♢</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">100%</div>
                    </div>
                    <div className="bg-white p-2 rounded-md shadow-sm flex items-center justify-between">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">{t('sets.boosters.others')}</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">0%</div>
                    </div>
                  </div>
                </div>
                
                {/* Position 4 */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-3 rounded-lg border border-purple-100">
                  <h5 className="text-xs sm:text-sm font-semibold text-indigo-700 mb-2 flex items-center">
                    <span className="inline-flex items-center justify-center bg-indigo-100 text-indigo-800 w-5 h-5 sm:w-6 sm:h-6 rounded-full mr-2 font-bold text-xs sm:text-sm">4</span>
                    {t('sets.boosters.position_4')}
                  </h5>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">♢♢</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">90%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">♢♢♢</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">5%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">♢♢♢♢</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">1.67%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">☆</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">2.57%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">☆☆</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">0.5%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">☆☆☆</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">0.22%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">✧</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">0.714%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">✧✧</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">0.333%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">♛</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">0.04%</div>
                    </div>
                  </div>
                </div>
                
                {/* Position 5 */}
                <div className="bg-gradient-to-r from-indigo-50 to-violet-50 p-3 rounded-lg border border-indigo-100">
                  <h5 className="text-xs sm:text-sm font-semibold text-indigo-700 mb-2 flex items-center">
                    <span className="inline-flex items-center justify-center bg-indigo-100 text-indigo-800 w-5 h-5 sm:w-6 sm:h-6 rounded-full mr-2 font-bold text-xs sm:text-sm">5</span>
                    {t('sets.boosters.position_5')}
                  </h5>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">♢♢</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">60%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">♢♢♢</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">20%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">♢♢♢♢</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">6.66%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">☆</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">10.29%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">☆☆</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">2%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">☆☆☆</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">0.89%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">✧</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">2.857%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">✧✧</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">1.333%</div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                      <div className="text-xs sm:text-sm font-medium text-gray-700">♛</div>
                      <div className="font-semibold text-xs sm:text-sm text-indigo-600">0.16%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Filtres avancés */}
        <div className="mt-3">
          <div 
            className="bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-3 rounded-lg shadow-sm cursor-pointer"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <h3 className="text-sm font-semibold text-white flex items-center justify-between">
              <div className="flex items-center">
                <Filter className="w-4 h-4 mr-2" />
                {t('sets.boosters.advanced_filters')}
              </div>
              {showAdvancedFilters ? (
                <ChevronUp className="w-4 h-4 text-white" />
              ) : (
                <ChevronDown className="w-4 h-4 text-white" />
              )}
            </h3>
          </div>
          
          {showAdvancedFilters && (
            <div className="mt-3 p-4 sm:p-5 bg-white rounded-lg border border-indigo-100 shadow-sm">
              <h4 className="text-sm sm:text-base font-semibold text-indigo-800 mb-4">{t('sets.boosters.card_types')}</h4>
              
              <div className="space-y-3">
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="p-3 border-b border-gray-100 bg-gray-50">
                    <h5 className="text-xs sm:text-sm font-medium text-gray-700">{t('sets.boosters.search_criteria')}</h5>
                  </div>
                  <div className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="showMissingOnly"
                          name="cardFilter"
                          checked={filterOptions?.showMissing}
                          onChange={() => onFilterChange?.({
                            showMissing: true,
                            showLessThanTwo: false,
                            excludeTradable: filterOptions?.excludeTradable || false
                          })}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <label htmlFor="showMissingOnly" className="ml-2 block text-sm font-medium text-gray-700">
                          {t('sets.boosters.missing_only')}
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="showLessThanTwo"
                          name="cardFilter"
                          checked={filterOptions?.showLessThanTwo}
                          onChange={() => onFilterChange?.({
                            showMissing: false,
                            showLessThanTwo: true,
                            excludeTradable: filterOptions?.excludeTradable || false
                          })}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <label htmlFor="showLessThanTwo" className="ml-2 block text-sm font-medium text-gray-700">
                          {t('sets.boosters.missing_or_incomplete')}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="p-3 border-b border-gray-100 bg-gray-50">
                    <h5 className="text-xs sm:text-sm font-medium text-gray-700">{t('sets.boosters.additional_options')}</h5>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="excludeTradable"
                        checked={filterOptions?.excludeTradable}
                        onChange={(e) => onFilterChange?.({
                          showMissing: filterOptions?.showMissing || false,
                          showLessThanTwo: filterOptions?.showLessThanTwo || false,
                          excludeTradable: e.target.checked
                        })}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="excludeTradable" className="ml-2 block text-sm font-medium text-gray-700">
                        {t('sets.boosters.exclude_tradable')}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="flex items-center gap-2 bg-white/50 p-2 rounded-md">
            <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium">1-3</div>
            <div>
              <p className="font-medium text-gray-700">{t('sets.boosters.positions_explanation.stats_1_3')}</p>
              <p className="text-gray-500 text-xs">{t('sets.boosters.positions_explanation.new_cards_1_3')}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 bg-white/50 p-2 rounded-md">
            <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium">4</div>
            <div>
              <p className="font-medium text-gray-700">{t('sets.boosters.positions_explanation.position_4')}</p>
              <p className="text-gray-500 text-xs">{t('sets.boosters.positions_explanation.new_card_4')}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 bg-white/50 p-2 rounded-md">
            <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium">5</div>
            <div>
              <p className="font-medium text-gray-700">{t('sets.boosters.positions_explanation.position_5')}</p>
              <p className="text-gray-500 text-xs">{t('sets.boosters.positions_explanation.new_card_5')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:space-y-6">
        {/* Pack Recommandé */}
        <div>
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-xs sm:text-sm font-medium text-gray-500">{t('sets.boosters.recommended_pack')}</h3>
            <div className="text-xs font-medium px-2 py-0.5 sm:py-1 bg-indigo-50 text-indigo-700 rounded-full">
              {t('sets.boosters.best_chance')}
            </div>
          </div>
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-3 sm:p-4 border border-indigo-100/50">
            <div className="flex items-start gap-3 sm:gap-4">
              {bestPack.packImage && (
                <img
                  src={`${SUPABASE_IMAGE_BASE_URL}${bestPack.packImage}`}
                  alt={bestPack.pack}
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain rounded-lg flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {bestPack.setImg && (
                    <img
                      src={`${SUPABASE_IMAGE_BASE_URL}${bestPack.setImg}`}
                      alt={bestPack.setName}
                      className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                    />
                  )}
                  <h4 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                    {bestPack.pack} <span className="text-xs sm:text-sm text-gray-500">({bestPack.setName})</span>
                  </h4>
                </div>
                <div className="mt-1">
                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <span className="text-gray-600">
                      {t('sets.boosters.global_probability')}
                    </span>
                    <span className="text-indigo-600 font-medium">
                      {formatPercent(bestPack.global)}
                    </span>
                  </div>
                  <div className="mt-1.5 sm:mt-2 w-full bg-white/50 rounded-full h-1.5 sm:h-2">
                    <div
                      className="bg-indigo-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                      style={{ width: `${bestPack.global * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="my-2 sm:my-3 border-t border-indigo-100/50" />

            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <div className="bg-white/50 rounded-lg p-2 sm:p-3">
                <div className="flex justify-center mb-1">
                  <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium text-xs">1-3</div>
                </div>
                <p className="text-sm sm:text-lg font-semibold text-indigo-600 text-center">{formatPercent(bestPack.firstThreeCards)}</p>
              </div>
              <div className="bg-white/50 rounded-lg p-2 sm:p-3">
                <div className="flex justify-center mb-1">
                  <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium text-xs">4</div>
                </div>
                <p className="text-sm sm:text-lg font-semibold text-indigo-600 text-center">{formatPercent(bestPack.fourthCard)}</p>
              </div>
              <div className="bg-white/50 rounded-lg p-2 sm:p-3">
                <div className="flex justify-center mb-1">
                  <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium text-xs">5</div>
                </div>
                <p className="text-sm sm:text-lg font-semibold text-indigo-600 text-center">{formatPercent(bestPack.fifthCard)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Autres Packs */}
        {otherPacks.length > 0 && (
                <div>
            <div className="flex items-center justify-between">
              <h3 className="text-xs sm:text-sm font-medium text-gray-500">{t('sets.boosters.other_packs')}</h3>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-xs font-medium px-2 py-1 text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                {isExpanded ? (
                  <>
                    {t('sets.boosters.hide')}
                    <ChevronUp className="w-3 h-3" />
                  </>
                ) : (
                  <>
                    {t('sets.boosters.view')} {otherPacks.length} {t('sets.boosters.packs')}
                    <ChevronDown className="w-3 h-3" />
                  </>
                )}
              </button>
                </div>

            <div className={`mt-2 sm:mt-3 space-y-2 sm:space-y-3 ${isExpanded ? 'block' : 'hidden'} sm:block`}>
              {otherPacks.map((pack) => (
                <div key={`${pack.setId}-${pack.pack}`} className="bg-gray-50 rounded-lg p-2 sm:p-3 border border-gray-100">
                  <div className="flex items-start gap-2 sm:gap-3">
                    {(pack as PackWithSet).packImage && (
                      <img
                        src={`${SUPABASE_IMAGE_BASE_URL}${(pack as PackWithSet).packImage}`}
                        alt={pack.pack}
                        className="w-10 h-10 sm:w-12 sm:h-12 object-contain rounded-lg flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 sm:gap-2">
                        {(pack as PackWithSet).setImg && (
                          <img
                            src={`${SUPABASE_IMAGE_BASE_URL}${(pack as PackWithSet).setImg}`}
                            alt={(pack as PackWithSet).setName}
                            className="w-3 h-3 sm:w-4 sm:h-4 object-contain"
                          />
                        )}
                        <h4 className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                          {pack.pack} <span className="text-xs text-gray-500">({pack.setName})</span>
                        </h4>
              </div>
                      <div className="mt-1 sm:mt-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">
                            {t('sets.boosters.global_probability')}
                          </span>
                          <span className="text-indigo-600 font-medium">
                            {formatPercent(pack.global)}
                          </span>
            </div>
                        <div className="mt-1 sm:mt-1.5 h-1 sm:h-1.5 bg-gray-200 rounded-full">
                          <div
                            className="bg-indigo-600 h-1 sm:h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${pack.global * 100}%` }}
                          />
          </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-1 sm:gap-2 mt-2 sm:mt-3">
                    <div className="text-center bg-white/30 rounded-md p-1">
                      <div className="flex justify-center mb-1">
                        <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium text-xs">1-3</div>
                      </div>
                      <p className="text-xs sm:text-sm font-medium text-indigo-600" title={t('sets.boosters.positions_explanation.new_cards_1_3')}>{formatPercent(pack.firstThreeCards)}</p>
                    </div>
                    <div className="text-center bg-white/30 rounded-md p-1">
                      <div className="flex justify-center mb-1">
                        <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium text-xs">4</div>
                      </div>
                      <p className="text-xs sm:text-sm font-medium text-indigo-600" title={t('sets.boosters.positions_explanation.new_card_4')}>{formatPercent(pack.fourthCard)}</p>
                    </div>
                    <div className="text-center bg-white/30 rounded-md p-1">
                      <div className="flex justify-center mb-1">
                        <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium text-xs">5</div>
                      </div>
                      <p className="text-xs sm:text-sm font-medium text-indigo-600" title={t('sets.boosters.positions_explanation.new_card_5')}>{formatPercent(pack.fifthCard)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 